import React, { useState, useRef, useEffect } from 'react';
import './Shop.css';
import { useAuth } from '../utils/AuthContext';
import { useAudio } from '../utils/AudioContext';
import { CASES, SKINS, BUNDLES, getSkin, RARITIES, DAILY_REWARDS, COIN_PACKS } from './ShopConfig'; 
import { 
    Coins, Plus, Gift, Box, Zap, Gem, Music, Circle, Square, 
    Lock, Play, X, Flame, Info, ChevronLeft, ChevronRight,
    LogIn, Save
} from 'lucide-react';
import { 
    playCollectSound, 
    playBumpSound, 
    playGameOverSound, 
    playBlingSound,
    playUiHoverSound,
    playUiOpenSound,
    playUiConfirmSound,
    playUiCloseSound,
    // New Imports
    playUiClickSound,
    playEquipSound,
    playCashRegisterSound
} from '../utils/sounds';

// ... (Effect imports if any)

function Shop({ onClose, onOpenLogin, onAddEffect }) {
    const { currentUser, userData, purchaseCase, equipSkin, claimDailyGift, purchaseBundle, purchaseCoinPack, initializeStarterOffer } = useAuth();
    const { sfxMuted } = useAudio();

    // State
    const [activeTab, setActiveTab] = useState('market'); 
    const [inventoryFilter, setInventoryFilter] = useState('player'); 
    const [showCurrency, setShowCurrency] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    
    // Exit Logic State
    const [exitType, setExitType] = useState(null); 

    // Roulette State
    const [isSpinning, setIsSpinning] = useState(false);
    const [spinState, setSpinState] = useState(null); 
    const [winPopupVisible, setWinPopupVisible] = useState(false);
    
    // Daily Reward State
    const [dailyRewardPopup, setDailyRewardPopup] = useState(null);

    // Bundle Success State
    const [bundleSuccessPopup, setBundleSuccessPopup] = useState(null);

    // Bundle Timer State
    const [starterBundleTimeLeft, setStarterBundleTimeLeft] = useState(null);
    const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

    // REFS
    const stripRef = useRef(null);
    const soundRef = useRef(null);
    const spinTimeoutRef = useRef(null);
    const renderTimeoutRef = useRef(null);

    // Odds State
    const [showOdds, setShowOdds] = useState(null); 
    const oddsScrollRef = useRef(null); 

    // Inventory State
    const [selectedInvItem, setSelectedInvItem] = useState(null);

    const CASE_STYLES = {
        'starter_case': { style: 'case-common', icon: Box },
        'elite_case': { style: 'case-rare', icon: Zap },
        'cosmic_case': { style: 'case-epic', icon: Gem },
        'music_case': { style: 'case-music', icon: Music },
    };

    // CLEANUP
    useEffect(() => {
        return () => {
            if (soundRef.current) clearTimeout(soundRef.current);
            if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
            if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);
        };
    }, []);

    // INIT OFFER FOR LEGACY USERS
    useEffect(() => {
        initializeStarterOffer();
    }, [initializeStarterOffer]);

    // PLAY OPEN SOUND
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TIMER LOGIC FOR STARTER BUNDLE
    useEffect(() => {
        const checkTime = () => {
            if (!userData) return;
            let startTime = userData.createdAt || userData.offerStartedAt;
            if (!startTime) {
                 startTime = Date.now(); 
            }
            const expiresAt = startTime + (72 * 60 * 60 * 1000); 
            const now = Date.now();
            const diff = expiresAt - now;

            if (diff > 0) {
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                setStarterBundleTimeLeft(`${hours.toString().padStart(2,'0')}:${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}`);
            } else {
                setStarterBundleTimeLeft('EXPIRED');
            }
        };

        const timer = setInterval(checkTime, 1000);
        checkTime(); 
        return () => clearInterval(timer);
    }, [userData]);

    const getSkinClass = (skin) => {
        if (!skin) return 'skin-basic';
        if (skin.cssClass) return skin.cssClass;
        return 'skin-basic';
    };

    const getRarityColor = (r) => RARITIES[r]?.color || '#9ca3af';

    // --- ANIMATION & CLOSE LOGIC ---
    const handleClose = () => {
        if (spinState || isSpinning || winPopupVisible || dailyRewardPopup || bundleSuccessPopup) return;
        playUiCloseSound(sfxMuted); 
        setExitType('close');
        setIsExiting(true);
    };

    // New handler for the Login Button
    const handleLoginClick = () => {
        playUiConfirmSound(sfxMuted); // Action intent
        setExitType('login');
        setIsExiting(true);
    };

    const handleAnimationEnd = (e) => {
        if (e.target !== e.currentTarget && !e.target.classList.contains('shop-modal')) return;

        if (e.target.classList.contains('shop-wrapper') || e.target.classList.contains('shop-modal')) {
            if (isExiting) {
                if (exitType === 'login' && onOpenLogin) {
                    onOpenLogin();
                } else {
                    onClose();
                }
            }
        }
    };

    const scrollOdds = (direction) => {
        playUiHoverSound(sfxMuted); 
        if (oddsScrollRef.current) {
            const amount = 120;
            oddsScrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
        }
    };

    const getAggregatedOdds = (caseId) => {
        const c = CASES.find(x => x.id === caseId);
        if (!c) return [];
        const caseSkins = SKINS.filter(s => c.contents.includes(s.id));
        let totalWeight = 0;
        caseSkins.forEach(s => totalWeight += RARITIES[s.rarity].probability);

        const rarityGroups = {};
        caseSkins.forEach(s => {
            const r = s.rarity;
            if (!rarityGroups[r]) rarityGroups[r] = 0;
            rarityGroups[r] += (RARITIES[r].probability / totalWeight) * 100;
        });

        return Object.keys(rarityGroups).map(key => ({
            id: key,
            name: RARITIES[key].name,
            color: RARITIES[key].color,
            percent: rarityGroups[key].toFixed(1)
        })).sort((a,b) => parseFloat(b.percent) - parseFloat(a.percent));
    };

    // --- DAILY GIFT LOGIC ---
    const isDailyAvailable = () => {
        if (!userData) return false;
        const lastClaimed = userData.dailyGift?.lastClaimed || 0;
        const now = new Date();
        const last = new Date(lastClaimed);
        return last.toDateString() !== now.toDateString();
    };

    const getStreak = () => {
         if (!userData) return 0;
         const lastClaimed = userData.dailyGift?.lastClaimed || 0;
         const now = new Date();
         const last = new Date(lastClaimed);
         
         const yesterday = new Date();
         yesterday.setDate(yesterday.getDate() - 1);
         
         if (last.toDateString() !== now.toDateString() && last.toDateString() !== yesterday.toDateString()) {
             return 0;
         }
         return userData.dailyGift?.streak || 0;
    };
    
    const getCurrentRewardAmount = () => {
        const streak = getStreak();
        if (isDailyAvailable()) {
            return DAILY_REWARDS[streak] || DAILY_REWARDS[0];
        }
        return DAILY_REWARDS[streak % 7] || DAILY_REWARDS[0];
    };

    const handleClaimDaily = async () => {
        if (spinState || isSpinning || winPopupVisible || dailyRewardPopup || bundleSuccessPopup) return;
        
        if (!isDailyAvailable()) return;
        
        playUiConfirmSound(sfxMuted); // Action intent

        const result = await claimDailyGift();
        if (result.success) {
            playBlingSound(sfxMuted); 
            setDailyRewardPopup({ amount: result.reward, streak: result.streak });
            if (onAddEffect) onAddEffect({ type: 'rain', count: 150 });
        }
    };

    // --- ROULETTE LOGIC ---
    const handleBuyCase = async (caseObj) => {
        if (spinState || isSpinning || winPopupVisible || dailyRewardPopup || bundleSuccessPopup) return;
        
        playUiConfirmSound(sfxMuted); // Buying is an action

        if ((userData?.money || 0) < caseObj.price) {
            playGameOverSound(sfxMuted);
            return;
        }

        const result = await purchaseCase(caseObj.id);
        if (result.success) {
            prepareSpin(caseObj, result.reward, result.isDuplicate, result.refundAmount);
        }
    };

    const prepareSpin = (caseInfo, reward, isDuplicate, refundAmount) => {
        const tape = generateTape(reward.id, caseInfo.contents);
        setSpinState({ tape, reward, caseInfo, isDuplicate, refundAmount });
        setWinPopupVisible(false);
        setIsSpinning(false); 
        
        if (stripRef.current) {
            stripRef.current.style.transition = 'none';
            stripRef.current.style.transform = 'translateX(0px)';
        }
    };

    const triggerSpin = () => {
        if (!spinState) return;
        playUiConfirmSound(sfxMuted); // Spinning is an action
        setIsSpinning(true);

        if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);

        renderTimeoutRef.current = setTimeout(() => {
            if (stripRef.current) {
                const centerOffset = 3355; 
                const jitter = Math.floor(Math.random() * 84) - 42; 
                const targetX = -(centerOffset) + jitter;
                
                stripRef.current.style.transition = 'transform 7s cubic-bezier(0.05, 0, 0, 1)';
                stripRef.current.style.transform = `translateX(${targetX}px)`;
                
                playTicker(7000);

                if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
                spinTimeoutRef.current = setTimeout(() => {
                    setIsSpinning(false);
                    setWinPopupVisible(true);
                    playBlingSound(sfxMuted); 
                    spinTimeoutRef.current = null;
                }, 7500);
            }
        }, 50);
    };

    const generateTape = (winnerId, contents) => {
        const tape = [];
        const winner = getSkin(winnerId);
        
        const possibleSkins = contents.map(id => getSkin(id));
        const sortedSkins = [...possibleSkins].sort((a, b) => 
            RARITIES[b.rarity].value - RARITIES[a.rarity].value
        );

        const tease1 = sortedSkins[0]; 
        const tease2 = sortedSkins[1] || sortedSkins[0]; 

        for (let i = 0; i < 40; i++) {
            if (i === 30) {
                tape.push(winner);
            } else if (i === 29) {
                tape.push(tease1.id === winner.id ? tease2 : tease1);
            } else if (i === 31) {
                tape.push(tease2.id === winner.id ? tease1 : tease2);
            } else {
                const randId = contents[Math.floor(Math.random() * contents.length)];
                tape.push(getSkin(randId));
            }
        }
        return tape;
    };

    const playTicker = (duration) => {
        if (sfxMuted) return;
        let time = 0;
        let interval = 50;
        const tick = () => {
            playBumpSound(sfxMuted);
            interval *= 1.1;
            time += interval;
            if (time < duration) {
                soundRef.current = setTimeout(tick, interval);
            }
        };
        tick();
    };

    const handleSkip = () => {
        if (!isSpinning) return;
        playUiConfirmSound(sfxMuted);
        
        if (soundRef.current) clearTimeout(soundRef.current);
        if (spinTimeoutRef.current) { clearTimeout(spinTimeoutRef.current); spinTimeoutRef.current = null; }
        if (renderTimeoutRef.current) { clearTimeout(renderTimeoutRef.current); renderTimeoutRef.current = null; }
        
        if (stripRef.current) {
             const centerOffset = 3355; 
             stripRef.current.style.transition = 'none';
             void stripRef.current.offsetWidth;
             stripRef.current.style.transform = `translateX(-${centerOffset}px)`;
        }
        
        setIsSpinning(false);
        setWinPopupVisible(true);
        playBlingSound(sfxMuted); 
    };

    const resetRoulette = () => {
        if (soundRef.current) clearTimeout(soundRef.current);
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);

        playUiCloseSound(sfxMuted); 
        setSpinState(null);
        setWinPopupVisible(false);
        setIsSpinning(false);
    };

    // --- BUNDLE PURCHASE ---
    const handleBuyBundle = async (bundle) => {
        if (isLocked || isCreatingCheckout) return;

        playUiConfirmSound(sfxMuted);
        setIsCreatingCheckout(true);
        try {
            const result = await purchaseBundle(bundle.id);
            if (result.success) {
                if (!result.redirecting) {
                    playCollectSound(sfxMuted);
                    setBundleSuccessPopup({ bundle });
                    if (bundle.contents?.coins > 0 && onAddEffect) {
                         onAddEffect({ type: 'rain', count: 150 });
                    }
                }
            } else {
                playGameOverSound(sfxMuted);
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
            playGameOverSound(sfxMuted);
        } finally {
            setIsCreatingCheckout(false);
        }
    };

    // --- COIN PACK PURCHASE ---
    const handleBuyCoinPack = async (pack) => {
        if (isLocked || isCreatingCheckout) return;

        playUiConfirmSound(sfxMuted);
        setIsCreatingCheckout(true);
        try {
            const result = await purchaseCoinPack(pack.id);
            if (!result.success && !result.redirecting) {
                 playGameOverSound(sfxMuted);
            }
        } catch (error) {
            console.error("Error buying coins:", error);
            setIsCreatingCheckout(false);
        }
    };

    // --- INVENTORY LOGIC ---
    const selectInvItem = (item) => {
        playUiClickSound(sfxMuted); // Clicking item is selection
        setSelectedInvItem(item);
    };

    const handleEquip = () => {
        if (!selectedInvItem) return;
        
        // Use new Equip sound here
        playEquipSound(sfxMuted);

        const skinId = selectedInvItem.id;
        // Default skins are always owned
        const isOwned = (userData?.skins?.owned?.[skinId]) || skinId === 'default' || skinId === 'tile_default';
        
        if (isOwned) {
            equipSkin(skinId);
        }
    };

    // Helper to get correct equipped state based on type
    const isEquipped = (skin) => {
        if (skin.type === 'tile') {
            return (userData?.skins?.equippedTile || 'tile_default') === skin.id;
        }
        return (userData?.skins?.equippedPlayer || 'default') === skin.id;
    };

    const getDisplayedSkins = () => {
        return SKINS.filter(s => (s.type || 'player') === inventoryFilter);
    };

    const getUnlockedCount = () => {
        const displayed = getDisplayedSkins();
        return displayed.filter(s => (userData?.skins?.owned?.[s.id]) || s.id === 'default' || s.id === 'tile_default').length;
    };

    const isLocked = spinState || isSpinning || winPopupVisible || dailyRewardPopup || bundleSuccessPopup;

    const getShapeClass = (type) => type === 'tile' ? 'skin-square' : 'skin-circle';

    // --- LOGIN WALL RENDER ---
    if (!currentUser) {
        return (
            <div 
                className={`shop-wrapper ${isExiting ? 'fade-out' : 'fade-in'}`}
                onAnimationEnd={handleAnimationEnd}
                style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
            >
                <div className={`shop-modal auth-prompt-modal enhanced ${isExiting ? 'pop-out' : ''}`}>
                    {/* ... (Keep existing login UI) ... */}
                    <div className="auth-header">
                        <div className="icon-glow-container">
                            <Box size={48} className="c-purple" />
                        </div>
                        <h2>Unlock the Shop</h2>
                        <p className="subtitle">Sign in to collect skins and rewards!</p>
                    </div>

                    <div className="feature-grid">
                        <div className="feature-item">
                            <div className="f-icon bg-purple"><Gem size={20} /></div>
                            <div className="f-text">
                                <span>Exclusive Skins</span>
                                <small>Customize your look</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-green"><Save size={20} /></div>
                            <div className="f-text">
                                <span>Cloud Save</span>
                                <small>Keep your inventory safe</small>
                            </div>
                        </div>
                         <div className="feature-item">
                            <div className="f-icon bg-yellow"><Gift size={20} /></div>
                            <div className="f-text">
                                <span>Daily Rewards</span>
                                <small>Free coins every day</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-blue"><Coins size={20} /></div>
                            <div className="f-text">
                                <span>Coin Packs</span>
                                <small>Boost your progress</small>
                            </div>
                        </div>
                    </div>

                    <div className="auth-actions">
                        <button 
                            onClick={handleLoginClick} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            className="btn-login-pulse"
                        >
                            <LogIn size={20} />
                            <span>Sign In / Register</span>
                        </button>
                        <button 
                            onClick={handleClose} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            className="btn-text-only"
                        >
                            Maybe Later
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- MAIN CONTENT ---
    return (
        <div 
            className={`shop-wrapper ${isExiting ? 'fade-out' : 'fade-in'}`}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className={`shop-modal ${isExiting ? 'pop-out' : ''}`}>
                
                {/* Header */}
                <div className="shop-header">
                    <div className="header-title">
                        <span className="c-g">S</span><span className="c-b">H</span><span className="c-r">O</span><span className="c-y">P</span>
                    </div>
                    <div className="currency-wrapper">
                        <div className="currency-pill">
                            <Coins size={16} />
                            <span>{userData?.money || 0}</span>
                            <button 
                                className="btn-plus" 
                                onClick={() => { 
                                    playCashRegisterSound(sfxMuted); // Use Cash Register Sound
                                    setShowCurrency(true); 
                                }} 
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                disabled={isLocked}
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                        <button 
                            className="btn-close-shop" 
                            onClick={handleClose} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            disabled={isLocked}
                            style={{ opacity: isLocked ? 0.3 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tabs-container">
                    <button 
                        className={`tab-btn ${activeTab === 'market' ? 'active' : ''}`} 
                        onClick={() => { 
                            if (!isLocked) { 
                                playUiClickSound(sfxMuted); // Switching tabs -> Random Click
                                setActiveTab('market'); 
                                setSelectedInvItem(null); 
                            } 
                        }}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        style={{ opacity: isLocked ? 0.5 : 1 }}
                    >Market</button>
                    <button 
                        className={`tab-btn ${activeTab === 'inventory' ? 'active' : ''}`} 
                        onClick={() => { 
                            if (!isLocked) { 
                                playUiClickSound(sfxMuted); // Switching tabs -> Random Click
                                setActiveTab('inventory'); 
                                setSelectedInvItem(null); 
                            } 
                        }}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        style={{ opacity: isLocked ? 0.5 : 1 }}
                    >Inventory</button>
                </div>

                {/* MARKET TAB */}
                {activeTab === 'market' && (
                    <div className="content-area">
                        {BUNDLES.map(bundle => {
                            const isPurchased = userData?.bundles?.[bundle.id];
                            if (isPurchased) return null; 

                            const isExpired = starterBundleTimeLeft === 'EXPIRED';
                            if (bundle.id === 'starter_bundle' && isExpired) return null; 

                            return (
                                <div key={bundle.id} className={`offer-banner ${bundle.cssClass || ''}`}>
                                    <div className="offer-header">
                                        <h4>{bundle.title}</h4>
                                        {bundle.id === 'starter_bundle' && starterBundleTimeLeft && (
                                            <span className="offer-timer">{starterBundleTimeLeft}</span>
                                        )}
                                    </div>
                                    <div className="offer-contents">
                                        {bundle.contentDisplay.map((item, idx) => (
                                            <div key={idx} className="offer-item">
                                                {item.type === 'coin' && <Coins size={24} />}
                                                {item.type === 'skin' && <div className={`skin-circle ${getSkinClass(getSkin(item.skinId))}`}></div>}
                                                {item.type === 'feature' && <Zap size={24} />} 
                                                <div>{item.amount || item.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="offer-footer">
                                        <div className="value-tag">{bundle.valueTag}</div>
                                        <button 
                                            className="offer-btn" 
                                            onClick={() => handleBuyBundle(bundle)}
                                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                            disabled={isCreatingCheckout}
                                        >
                                            {isCreatingCheckout ? (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                    <div className="spinner"></div>
                                                    <span style={{color: '#6366f1'}}>LOADING...</span>
                                                </div>
                                            ) : (
                                                typeof bundle.price === 'number' ? (
                                                    <div style={{display:'flex',alignItems:'center',gap:4}}><Coins size={14}/> {bundle.price}</div>
                                                ) : bundle.price
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="case-grid">
                            <div 
                                className={`case-card case-daily`} 
                                onClick={handleClaimDaily}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                style={{
                                    opacity: isDailyAvailable() ? 1 : 0.5,
                                    cursor: isDailyAvailable() ? 'pointer' : 'default'
                                }}
                            >
                                <div className="case-img-wrapper"><Gift size={50} color={isDailyAvailable() ? "var(--c-green)" : "#64748b"} /></div>
                                <div className="case-name">Daily Gift</div>
                                <div className="price-badge">{isDailyAvailable() ? (
                                    <>
                                        <Coins size={12} style={{marginRight:4}} /> {getCurrentRewardAmount()}
                                    </>
                                ) : "CLAIMED"}</div>
                            </div>

                            {CASES.map(c => {
                                const styleData = CASE_STYLES[c.id] || CASE_STYLES['starter_case'];
                                const Icon = styleData.icon;
                                return (
                                    <div 
                                        key={c.id} 
                                        className={`case-card ${styleData.style}`} 
                                        onClick={() => handleBuyCase(c)}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        <button 
                                            className="info-btn" 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                playUiClickSound(sfxMuted); // Info button -> Random Click
                                                setShowOdds(c.id); 
                                            }}
                                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                        >
                                            <Info size={14} />
                                        </button>
                                        <div className="case-img-wrapper">
                                            <Icon size={50} />
                                        </div>
                                        <div className="case-name">{c.name}</div>
                                        <div className="price-badge">
                                            <Coins size={12} /> {c.price}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Streak Footer */}
                        <div className="streak-footer">
                            <div className="sf-icon"><Flame size={16} /><span>{getStreak()}</span></div>
                            <div className="sf-bar-container">
                                <div className="sf-label"><span>Daily Streak</span><span>{getStreak()} / 7 Days</span></div>
                                <div className="sf-track">
                                    {DAILY_REWARDS.map((r, i) => {
                                        const day = i + 1;
                                        const streak = getStreak();
                                        const isActive = day <= streak;
                                        const isCurrent = day === streak; 
                                        return (
                                            <div 
                                                key={day} 
                                                className={`sf-seg ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                                            ></div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="sf-reward">
                                {getStreak() === 7 ? <Gem size={18} color="var(--c-purple)" /> : <Gift size={18} />}
                            </div>
                        </div>

                    </div>
                )}

                {/* INVENTORY TAB */}
                {activeTab === 'inventory' && (
                    <div className="content-area">
                        <div className="inv-section">
                            {/* Inventory Filter Tabs */}
                            <div className="inv-subtabs">
                                <button 
                                    className={`inv-subtab ${inventoryFilter === 'player' ? 'active' : ''}`}
                                    onClick={() => { 
                                        playUiClickSound(sfxMuted); // Random Click
                                        setInventoryFilter('player'); 
                                        setSelectedInvItem(null); 
                                    }}
                                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                >
                                    <Circle size={14} /> Runners
                                </button>
                                <button 
                                    className={`inv-subtab ${inventoryFilter === 'tile' ? 'active' : ''}`}
                                    onClick={() => { 
                                        playUiClickSound(sfxMuted); // Random Click
                                        setInventoryFilter('tile'); 
                                        setSelectedInvItem(null); 
                                    }}
                                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                >
                                    <Square size={14} /> Walls
                                </button>
                            </div>

                            <div className="inv-header">
                                <span className="inv-title-wrap">Collection</span>
                                <span className="collection-count">
                                    {getUnlockedCount()} / {getDisplayedSkins().length}
                                </span>
                            </div>
                            
                            <div className="inv-grid">
                                {getDisplayedSkins().map(s => {
                                    const owned = (userData?.skins?.owned?.[s.id]) || s.id === 'default' || s.id === 'tile_default';
                                    const equipped = isEquipped(s);
                                    const isSelected = selectedInvItem?.id === s.id;
                                    const visualClass = getSkinClass(s);
                                    const shapeClass = getShapeClass(s.type);
                                    
                                    return (
                                        <div 
                                            key={s.id} 
                                            className={`inv-item ${owned ? `i-${s.rarity.toLowerCase()}` : 'locked'} ${equipped ? 'equipped' : ''} ${isSelected ? 'selected' : ''}`}
                                            onClick={() => selectInvItem(s)}
                                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                        >
                                            <div className={`${shapeClass} ${visualClass}`}></div>
                                            {!owned && <Lock size={16} style={{position:'absolute', color:'white', opacity:0.8}} />}
                                            {equipped && <div className="equipped-label">EQUIPPED</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* ITEM INSPECTOR */}
                <div className={`item-inspector ${selectedInvItem ? 'active' : ''}`}>
                    <div className="insp-details">
                        <div className="insp-icon">
                            {selectedInvItem && <div className={`${getShapeClass(selectedInvItem.type)} ${getSkinClass(selectedInvItem)}`}></div>}
                        </div>
                        <div className="insp-text">
                            <span className="rarity-tag" style={{color: getRarityColor(selectedInvItem?.rarity)}}>
                                {selectedInvItem?.rarity}
                            </span>
                            <h4>{selectedInvItem?.name}</h4>
                            <p>{selectedInvItem?.description || "No description."}</p>
                        </div>
                    </div>
                    <button 
                        className={`btn-equip ${ (isEquipped(selectedInvItem || {}) || !((userData?.skins?.owned?.[selectedInvItem?.id]) || selectedInvItem?.id === 'default' || selectedInvItem?.id === 'tile_default')) ? 'disabled' : ''}`} 
                        onClick={handleEquip}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        {isEquipped(selectedInvItem || {}) 
                            ? "EQUIPPED" 
                            : ((userData?.skins?.owned?.[selectedInvItem?.id] || selectedInvItem?.id === 'default' || selectedInvItem?.id === 'tile_default') ? "EQUIP" : "LOCKED")}
                    </button>
                </div>

                {/* ROULETTE OVERLAY */}
                <div className={`spin-overlay ${isLocked && !dailyRewardPopup && !bundleSuccessPopup ? 'active' : ''}`}>
                    {!winPopupVisible && (
                        <button 
                            className="close-spin-btn" 
                            onClick={resetRoulette} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            style={{display: isSpinning ? 'none' : 'block'}}
                        >
                            <X size={20} />
                        </button>
                    )}
                    <div className="spin-title">Opening {spinState?.caseInfo?.name || "Case..."}</div>
                    <div className="roulette-viewport">
                        <div className="center-marker"></div>
                        <div className="center-line"></div>
                        <div className="roulette-strip" ref={stripRef}>
                            {spinState?.tape?.map((item, i) => (
                                <div key={i} className="roulette-item" style={{ borderBottomColor: getRarityColor(item.rarity) }}>
                                    <div className={`${getShapeClass(item.type)} ${getSkinClass(item)}`}></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {isSpinning && <button className="skip-btn" style={{display:'block'}} onClick={handleSkip}>SKIP ANIMATION</button>}
                    <button 
                        className="spin-btn" 
                        onClick={triggerSpin} 
                        disabled={isSpinning} 
                        style={{ opacity: winPopupVisible ? 0 : 1 }}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        {isSpinning ? "SPINNING..." : "SPIN!"}
                    </button>
                </div>

                {/* WIN POPUP (SKINS) */}
                <div className={`win-popup ${winPopupVisible ? 'show' : ''}`}>
                    <div className="sunburst"></div>
                    
                    <div className="win-content"> 
                        {spinState && (
                            <>
                                <h2 style={{marginBottom:10, color:'var(--c-yellow)', textShadow:'0 2px 10px rgba(0,0,0,0.5)'}}>YOU UNLOCKED!</h2>
                                {spinState.isDuplicate && <div className="duplicate-badge">DUPLICATE • +{spinState.refundAmount} COINS</div>}
                                <div className="rarity-badge" style={{ color: getRarityColor(spinState.reward.rarity), borderColor: getRarityColor(spinState.reward.rarity) }}>
                                    {spinState.reward.rarity}
                                </div>
                                <div className={`${getShapeClass(spinState.reward.type)} ${getSkinClass(spinState.reward)}`} style={{width:100, height:100, margin:'20px auto', boxShadow:`0 0 30px ${getRarityColor(spinState.reward.rarity)}`}}></div>
                                <h3 style={{marginBottom:25, fontSize:'1.4rem'}}>{spinState.reward.name}</h3>
                                <div className="win-actions">
                                    <button 
                                        className="btn-claim" 
                                        onClick={resetRoulette}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        CLAIM
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* DAILY REWARD POPUP */}
                <div className={`win-popup ${dailyRewardPopup ? 'show' : ''}`} style={{zIndex: 400}}>
                    <div className="sunburst"></div>
                    
                    <div className="win-content"> 
                        {dailyRewardPopup && (
                            <>
                                <h2 style={{marginBottom:10, color:'var(--c-yellow)', textShadow:'0 2px 10px rgba(0,0,0,0.5)'}}>DAILY GIFT!</h2>
                                <div style={{ marginBottom: 10, fontSize: '0.9rem', opacity: 0.8 }}>Day {dailyRewardPopup.streak} Streak</div>
                                <div style={{ margin: '30px auto' }}>
                                    <Coins size={80} color="#eab308" />
                                </div>
                                <h3 style={{marginBottom:25, fontSize:'2rem'}}>+{dailyRewardPopup.amount} Coins</h3>
                                <div className="win-actions">
                                    <button 
                                        className="btn-claim" 
                                        onClick={() => {
                                            playUiConfirmSound(sfxMuted);
                                            setDailyRewardPopup(null);
                                        }}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        CLAIM
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* BUNDLE SUCCESS POPUP */}
                <div className={`win-popup ${bundleSuccessPopup ? 'show' : ''}`} style={{zIndex: 400}}>
                    <div className="sunburst"></div>
                    
                    <div className="win-content"> 
                        {bundleSuccessPopup && (
                            <>
                                <h2 style={{marginBottom:10, color:'var(--c-yellow)', textShadow:'0 2px 10px rgba(0,0,0,0.5)'}}>BUNDLE UNLOCKED!</h2>
                                <div style={{ margin: '20px auto' }}>
                                    {/* Visualize Contents */}
                                    <div style={{display:'flex', justifyContent:'center', gap:15, flexWrap:'wrap'}}>
                                        {bundleSuccessPopup.bundle.contentDisplay.map((item, i) => (
                                            <div key={i} style={{display:'flex', flexDirection:'column', alignItems:'center', width: 70}}>
                                                {item.type === 'coin' && <Coins size={40} color="#eab308" />}
                                                {item.type === 'skin' && <div className={`skin-circle ${getSkinClass(getSkin(item.skinId))}`} style={{width:40, height:40}}></div>}
                                                {item.type === 'feature' && <Zap size={40} color="#6366f1" />}
                                                <div style={{fontSize: '0.8rem', marginTop: 5}}>{item.label || item.amount}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <h3 style={{marginBottom:25, fontSize:'1.2rem'}}>{bundleSuccessPopup.bundle.title} Acquired</h3>
                                <div className="win-actions">
                                    <button 
                                        className="btn-claim" 
                                        onClick={() => {
                                            playUiConfirmSound(sfxMuted);
                                            setBundleSuccessPopup(null);
                                        }}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        AWESOME
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ODDS OVERLAY */}
                <div className={`spin-overlay ${showOdds ? 'active' : ''}`} style={{zIndex: 300}}>
                    {showOdds && (
                        <div className="odds-card">
                            <h3 style={{marginBottom: 10, textTransform: 'uppercase'}}>{CASES.find(c => c.id === showOdds)?.name} ODDS</h3>
                            <div className="loot-preview-container">
                                <div className="loot-label">POSSIBLE LOOT</div>
                                <div className="loot-scroller-wrapper">
                                    <button className="scroll-btn left" onClick={() => scrollOdds('left')}><ChevronLeft size={16}/></button>
                                    <div className="loot-scroller" ref={oddsScrollRef}>
                                        {CASES.find(c => c.id === showOdds)?.contents.map(skinId => {
                                            const skin = getSkin(skinId);
                                            return (
                                                <div key={skinId} className="loot-item">
                                                    <div className={`${getShapeClass(skin.type)} ${getSkinClass(skin)} small`}></div>
                                                    <span style={{color: getRarityColor(skin.rarity)}}>{skin.name}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <button className="scroll-btn right" onClick={() => scrollOdds('right')}><ChevronRight size={16}/></button>
                                </div>
                            </div>
                            <div className="odds-list">
                                {getAggregatedOdds(showOdds).map(o => (
                                    <div key={o.id} className="odds-row">
                                        <div className="odds-label"><div className="dot" style={{background: o.color}}></div> {o.name}</div>
                                        <div style={{color: o.color}}>{o.percent}%</div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className="btn-close-odds" 
                                onClick={() => {
                                    playUiClickSound(sfxMuted); // Closing/clicking stuff -> Random Click (as per instruction "clicking on stuff")
                                    setShowOdds(null);
                                }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                CLOSE
                            </button>
                        </div>
                    )}
                </div>

                {/* CURRENCY OVERLAY */}
                <div className={`currency-overlay ${showCurrency ? 'active' : ''}`}>
                    <div className="currency-sheet">
                        <div className="sheet-header">
                            <h2 className="sheet-title">Get Coins</h2>
                            <button 
                                className="sheet-close-btn" 
                                onClick={() => {
                                    playUiClickSound(sfxMuted); // Clicking to close overlay -> Random Click
                                    setShowCurrency(false);
                                }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <button className="watch-ad-mega">
                            <div className="wad-inner">
                                <div className="ad-content">
                                    <div className="play-icon-circle"><Play size={24} fill="currentColor" /></div>
                                    <div className="ad-text">
                                        <div className="ad-title">WATCH AD</div>
                                        <div className="ad-reward">+100 <Coins size={18} /></div>
                                    </div>
                                </div>
                                <div className="ad-badge">FREE</div>
                            </div>
                        </button>

                        <div className="coin-grid">
                            {COIN_PACKS.map(pack => (
                                <div 
                                    key={pack.id} 
                                    className={`coin-pack ${pack.tag ? 'popular' : ''}`}
                                    onClick={() => handleBuyCoinPack(pack)}
                                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    style={{ opacity: isCreatingCheckout ? 0.7 : 1, cursor: isCreatingCheckout ? 'wait' : 'pointer' }}
                                >
                                    {pack.tag && (
                                        <div 
                                            className="pack-tag" 
                                            style={pack.tagColor ? { background: pack.tagColor, color: 'white' } : {}}
                                        >
                                            {pack.tag}
                                        </div>
                                    )}
                                    <div className="pack-icon">
                                        <Coins size={32 * (pack.iconScale || 1)} />
                                    </div>
                                    <div className="pack-amount">{pack.amount.toLocaleString()}</div>
                                    <div className="pack-price">
                                        {isCreatingCheckout ? "..." : pack.priceLabel}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default Shop;